import { useEffect, useMemo, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { Check, Copy, Download, Loader2, QrCode, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { MFASetupResponse } from '../types/auth.types';

function formatSetupKey(secret: string): string {
  return (
    secret
      .replace(/\s+/g, '')
      .toUpperCase()
      .match(/.{1,4}/g)
      ?.join(' ') ?? secret
  );
}

function downloadTextFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildBackupCodeFile(accountLabel: string, backupCodes: string[]): string {
  return [
    'UniTree backup codes',
    `Account: ${accountLabel}`,
    '',
    ...backupCodes,
    '',
    'Each code can be used once if your authenticator app is unavailable.',
  ].join('\n');
}

export function MfaTotpSetupPanel({
  setupDetails,
  accountLabel,
  codeField,
  disabled = false,
}: {
  setupDetails: MFASetupResponse;
  accountLabel: string;
  codeField: ReactNode;
  disabled?: boolean;
}) {
  const [qrResult, setQrResult] = useState<{
    value: string;
    dataUrl: string | null;
    error: boolean;
  }>({ value: '', dataUrl: null, error: false });
  const [showManualKey, setShowManualKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const qrValue = setupDetails.qrCodeUrl ?? setupDetails.qrCodeUri ?? '';
  const setupKey = useMemo(() => formatSetupKey(setupDetails.secret), [setupDetails.secret]);
  const qrCodeDataUrl = qrResult.value === qrValue ? qrResult.dataUrl : null;
  const qrError = !qrValue || (qrResult.value === qrValue && qrResult.error);

  useEffect(() => {
    let cancelled = false;

    if (!qrValue) {
      return;
    }

    QRCode.toDataURL(qrValue, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 224,
      color: {
        dark: '#020617',
        light: '#ffffff',
      },
    })
      .then((dataUrl) => {
        if (!cancelled) {
          setQrResult({ value: qrValue, dataUrl, error: false });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQrResult({ value: qrValue, dataUrl: null, error: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [qrValue]);

  const copySetupKey = async () => {
    if (!navigator.clipboard) {
      return;
    }
    await navigator.clipboard.writeText(setupDetails.secret);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
        <div className="rounded-md border bg-background p-4">
          <div className="flex items-start gap-3">
            <QrCode className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">Scan a QR code</p>
              <p className="mt-1 text-sm text-muted-foreground">
                In Google Authenticator, Microsoft Authenticator, or 1Password, add a new account
                and choose scan QR code.
              </p>
            </div>
          </div>
          <div className="mt-4 flex min-h-[244px] items-center justify-center rounded-md bg-white p-3">
            {qrCodeDataUrl ? (
              <img
                src={qrCodeDataUrl}
                alt="Authenticator setup QR code"
                className="h-56 w-56"
                draggable={false}
              />
            ) : qrError ? (
              <p className="max-w-56 text-center text-sm text-muted-foreground">
                QR code is unavailable. Use the setup key instead.
              </p>
            ) : (
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            )}
          </div>
          {!qrError ? (
            <Button
              type="button"
              variant="link"
              className="mt-2 w-full"
              onClick={() => setShowManualKey((value) => !value)}
              disabled={disabled}
            >
              {showManualKey ? 'Scan QR code instead' : "Can't scan it?"}
            </Button>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/20 p-4">
            <p className="text-sm font-semibold text-foreground">Enter the verification code</p>
            <p className="mt-1 text-sm text-muted-foreground">
              After adding the account, enter the current 6-digit code from the authenticator app.
            </p>
            <div className="mt-4">{codeField}</div>
          </div>

          {showManualKey || qrError ? (
            <div className="rounded-md border bg-background p-4">
              <p className="text-sm font-semibold text-foreground">Use a setup key</p>
              <ol className="mt-2 list-decimal space-y-1 pl-4 text-sm text-muted-foreground">
                <li>Choose enter a setup key in your authenticator app.</li>
                <li>Use {accountLabel} as the account name.</li>
                <li>Make sure time based is selected.</li>
              </ol>
              <div className="mt-3 rounded-md border bg-muted/30 p-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Setup key
                </Label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="min-w-0 flex-1 break-all font-mono text-sm font-semibold text-foreground">
                    {setupKey}
                  </code>
                  {navigator.clipboard ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => void copySetupKey()}
                      disabled={disabled}
                      aria-label="Copy setup key"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function MfaBackupCodesPanel({
  accountLabel,
  backupCodes,
  onContinue,
  continueLabel = 'Continue',
  disabled = false,
}: {
  accountLabel: string;
  backupCodes: string[];
  onContinue: () => void;
  continueLabel?: string;
  disabled?: boolean;
}) {
  const downloadBackupCodes = () => {
    downloadTextFile(
      `unitree-backup-codes-${accountLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.txt`,
      buildBackupCodeFile(accountLabel, backupCodes),
    );
  };

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-emerald-950">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Authenticator app verified</p>
            <p className="mt-1 text-sm">
              Save these backup codes now. They are shown only once and can recover access if your
              authenticator app is unavailable.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {backupCodes.map((code) => (
          <code
            key={code}
            className="rounded-md border bg-muted/30 px-3 py-2 text-center font-mono text-sm font-semibold"
          >
            {code}
          </code>
        ))}
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={downloadBackupCodes} disabled={disabled}>
          <Download className="mr-2 h-4 w-4" />
          Download backup codes
        </Button>
        <Button type="button" onClick={onContinue} disabled={disabled}>
          {continueLabel}
        </Button>
      </div>
    </div>
  );
}
